import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faFile } from '@fortawesome/free-regular-svg-icons';
import { faCheckCircle, faEllipsisV, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress, Divider, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { default as classNames, default as classnames } from 'classnames';
import { Filter, Loader, SGSBadge, SGSButton, SGSCard, SGSColumnFilter, SGSForm, SGSTab, SGSTabPanel, SGSTable, SGSTabs, SGSTextButton, SGSTooltip } from 'components';
import SGSModalBlue from 'components/Modal';
import SGSDrawer from 'components/SGSDrawer';
import { useAppContext } from 'core/contexts';
import { useRouteQuery } from 'core/hooks';
import { TypeRole, TypeTitre, UserFormData, Utilisateur } from 'core/models';
import { useCustomStyles } from 'core/mui/customStyles';
import { exporUtilisateurs } from 'core/services/utilisateurService';
import { addServerErrors } from 'core/utils/formValidation';
import { hasRole, isVisitorBackOffice } from 'core/utils/roles';
import { makeUniqueAndSort } from 'core/utils/utils';
import { FormDroits } from 'features/utilisateurs/components/form/formDroits';
import { FormInfos } from 'features/utilisateurs/components/form/formInfos';
import { FormPerimetre } from 'features/utilisateurs/components/form/formPerimetre';
import { useUserInfosContext } from 'features/utilisateurs/contexts/UtilisateurInfosProvider';
import { cloneDeep, delay } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Cell, ColumnInterface } from 'react-table';
import { toast } from 'react-toastify';
import { utilisateurService } from '../../../core/services';
import ModalErrorDeletingUser from '../Modals/ModalErrorDeletingUser';
import { useUtilisateurFromContext } from '../contexts/UtilisateurFormProvider';
import { useUserPerimetreContext } from '../contexts/UtilisateurPerimetreProvider';
import { responsablesFromUsers } from '../utils/common';
import styles from './Utilisateurs.module.scss';
import { FormAlerte } from './form/formAlerte';
import { FormCodeGestionnaire } from './form/formCodeGestionnaire';
import { FormValidation } from './form/validationSchema';

type Props = {};

const UsersList: React.FC<Props> = () => {
    const { t } = useTranslation();
    const { user, setToken } = useAppContext();
    const commonStyles = useCustomStyles();
    const { selectedUser, setSelectedUser, setIsFormSubmitted, isFormSubmitted, setUserConnected, isLoadingUser, setIsLoadingUser, setIsValidatedEmail, setIsValidatedLogin } =
        useUtilisateurFromContext();

    const [isFormdeleting, setIsFormdeleting] = useState(false);
    const { setSante, setPrevoyance, setPerimetre, perimetre, setSelectedColleges, setSelectedSouscrips, setLoadingPerimetre, setSouscrips, setColleges } = useUserPerimetreContext();
    const { role, setRole, setTitre, setSelectedResponsable, responsables, setResponsables, loading, setLoading } = useUserInfosContext();
    const [initialUsers, setInitialUsers] = useState<Utilisateur[]>([]);
    const [users, setUsers] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalErrorOpen, setIsModalErrorOpen] = useState(false);
    const [modalErrorTitle, setModalErrorTitle] = useState('');
    const [errorCode, setErrorCode] = useState();
    const query = useRouteQuery();
    const searchQueryValue = query.get('search');
    const [loadedUser, setLoadedUser] = useState(null);
    const [activeFilters, setActiveFilters] = useState<Filter[]>(
        searchQueryValue
            ? [
                  {
                      nom: searchQueryValue,
                  },
              ]
            : [],
    );

    const columns = useMemo<ColumnInterface<Utilisateur>[]>(
        () => [
            {
                id: '0',
                Header: '',
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: (row: Utilisateur) => `${row.accesWeb}`,
                minWidth: 50,
                maxWidth: 50,
                disableFilters: true,

                Cell: ({ row: { original: row } }: Cell<Utilisateur>) => {
                    return (
                        <SGSTooltip className="text-center" title={<Box>{row.accesWeb ? 'ACTIF' : 'INACTIF'}</Box>}>
                            <Box>{getStatusIcon(row.accesWeb ? 'ACTIF' : 'INACTIF')}</Box>
                        </SGSTooltip>
                    );
                },
                options: {
                    display: false,
                },
            },

            {
                Header: t('utilisateurs.list.nom') + ', ' + t('utilisateurs.list.prenom') + ' et ' + t('utilisateurs.list.fonction'),
                headerClassName: styles.tableBorderRight__header,
                disableFilters: true,
                className: `${styles.tableBorderRight__cell}`,
                accessor: 'nom',
                Cell: ({ row: { original: row } }: Cell<Utilisateur>) => {
                    return (
                        <Box>
                            <Box className={styles.strong}>
                                {row.nom} {row.prenom}
                            </Box>
                            <Box>{row.fonction}</Box>
                        </Box>
                    );
                },
            },
            {
                Header: t('utilisateurs.list.role'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell}`,
                accessor: 'role',
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Utilisateur>) => {
                    const color = row.role === 'ADMIN' ? 'orange' : row.role === 'RESPONSABLE' ? 'blue' : 'darkblue';
                    return (
                        <>
                            <SGSBadge color={color} size="small">
                                {row.role}
                            </SGSBadge>
                        </>
                    );
                },
            },
            {
                Header: t('utilisateurs.list.responsable'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell}`,
                accessor: 'responsable',
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<any>) => {
                    return (
                        <>
                            {row.responsable ? (
                                <span className="text-uppercase">
                                    {row.responsable.nom} {row.responsable.prenom}
                                </span>
                            ) : (
                                <div className="w-100 text-center">-</div>
                            )}
                        </>
                    );
                },
            },
            // {
            //     Header: t('utilisateurs.list.accesWeb'),
            //     headerClassName: styles.tableBorderRight__header,
            //     className: `${styles.tableBorderRight__cell}  justify-content-center text-center`,
            //     accessor: 'accesWeb',
            //     disableFilters: true,
            //     minWidth: 50,
            //     maxWidth: 50,
            //     Cell: ({ row: { original: row } }: Cell<Utilisateur>) => {
            //         return (
            //             <>
            //                 <SGSIconBadge image="" color={row.accesWeb ? 'green' : 'orange'}></SGSIconBadge>
            //             </>
            //         );
            //     },
            // },
            {
                Header: '',
                accessor: 'none' as any,
                className: 'p-0 align-items-stretch',
                disableFilters: true,
                disableSortBy: true,
                minWidth: 30,
                maxWidth: 30,
                Cell: ({ row }: Cell<Utilisateur>) => {
                    return (
                        <div className={styles.actions}>
                            <SGSTooltip title={<Box>{t('details')}</Box>}>
                                <div className={styles.actions__menu} onClick={(e) => handleDetails({ e, row })}>
                                    <FontAwesomeIcon icon={faEllipsisV} size="lg" />
                                </div>
                            </SGSTooltip>
                        </div>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t],
    );

    const filterableColumnLabels = {
        nom: { value: t('utilisateurs.list.nom'), type: 'input' },
        prenom: { value: t('utilisateurs.list.prenom'), type: 'input' },
        fonction: { value: t('utilisateurs.list.fonction'), type: 'input' },
        role: {
            value: t('utilisateurs.list.role'),
            type: 'autocomplete',
            options: [
                { value: 'ADMIN', label: 'Admin' },
                { value: 'RESPONSABLE', label: 'Responsable' },
                { value: 'UTILISATEUR', label: 'Utilisateur' },
            ],
            isLoading: false,
        },
        responsable: {
            value: t('utilisateurs.list.responsable'),
            type: 'autocomplete',
            options: responsables.map((r) => ({
                value: r.id,
                label: r.nom + ' ' + r.prenom,
            })),
            isLoading: false,
            key: 'responsable',
        },
    };

    const handleFilters = useCallback(() => {
        const activeFiltersKeys = activeFilters.reduce<string[]>((r, f) => [...r, ...Object.keys(f)], []);
        if (activeFiltersKeys.length) {
            let list = initialUsers.filter((e) => {
                const result = [];
                activeFiltersKeys.forEach((key) => {
                    const obj = activeFilters.find((x) => x[key]);
                    if (key !== 'responsable') {
                        const resp: any = obj[key];
                        if (obj) result.push(e[key].includes(resp.toUpperCase()));
                    } else {
                        const responsable = responsables.find((e) => e.nom + ' ' + e.prenom === obj.responsable);
                        if (responsable) result.push(e?.responsable?.id === responsable.id);
                    }
                });
                return result.includes(false) ? false : true;
            });

            setUsers(list);
        } else {
            setUsers(initialUsers);
        }
    }, [activeFilters, initialUsers, responsables]);

    const toggleDrawer = (open: boolean) => {
        setDrawerOpened(open);
    };

    const handleSelectedTab = async (_event: React.ChangeEvent<{}>, newValue: number) => {
        const isValid = await validateStep(selectedTab);
        if (!isValid) return;
        setSelectedTab(newValue);
    };

    const convertApiResponseToFormData = (r: Utilisateur) => {
        if (!r) return;
        // IsAdmin ? show all options
        const isAdmin = r.roles.includes('ROLE_ADMIN');
        // RISKS
        const risks = r.perimetre?.risques ?? [];
        const { souscriptIds, souscripOpts, souscripOptsSelected } = souscripOptionsAndSelection(r.perimetre?.souscripteurs, risks, perimetre, isAdmin);
        const { collegeOpts, collegeOptsSelected } = collegesOptionsAndSelection(r.perimetre?.colleges, risks, souscriptIds, perimetre, isAdmin);
        const sante = isAdmin ? true : r.perimetre?.risques ? r.perimetre.risques.includes('S') : false;
        const prevoyance = isAdmin ? true : r.perimetre?.risques ? r.perimetre.risques.includes('P') : false;

        const newSelectedUser: UserFormData = {
            id: r.id,
            nom: r.nom,
            prenom: r.prenom,
            titre: r.titre,
            telephone: r.telephone,
            email: r.email,
            fonction: r.fonction,
            role: r.role,
            responsable: r.responsable,
            accesWeb: r.accesWeb ? 'true' : 'false',
            login: r.login,
            codeGestionnaire: r.codeGestionnaire ?? '',
            codeGestionnaireMultiple: r.codeGestionnaireMultiple,
            perimetre: {
                risques: {
                    sante,
                    prevoyance,
                },
                souscripteurs: souscripOptsSelected,
                colleges: collegeOptsSelected,
                excludeVIP: r?.perimetre?.excludeVIP ?? false,
            },
            roles: r.roles,
            alertBia: {
                id: r.alertBia?.id ?? null,
                actif: r.alertBia?.actif ?? false,
                frequence: r.alertBia?.frequence.toString() ?? '1',
                lastNotification: r.alertBia?.lastNotification ?? null,
            },
        };

        setSouscrips(souscripOpts);
        setColleges(collegeOpts);
        setSelectedUser(newSelectedUser);
        delay(setIsLoadingUser, 30, false);
    };

    useEffect(() => {
        convertApiResponseToFormData(loadedUser);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedUser, perimetre]);

    const handleDetails = ({ e, row }: { e?: any; row: any }) => {
        if (e) {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
        }
        reset();
        setIsValidatedEmail(true);
        setIsValidatedLogin(true);
        setIsLoadingUser(true);
        toggleDrawer(true);
        setSelectedTab(0);

        utilisateurService
            .getUser(row.original.id)
            .then((response: Utilisateur) => {
                setLoadedUser(response);
            })
            .catch((err) => {
                // Check if it's a 404 error
                if (err.response && err.response.status === 404) {
                    toast.error("L'utilisateur n'existe pas");
                } else {
                    toast.error('Oups, une erreur est survenue');
                }
                setIsLoadingUser(false);
            });
    };

    const handleResponse = (users: Utilisateur[]) => {
        // Get list of responsable + admin
        const responsables = responsablesFromUsers(users);
        setResponsables(responsables);
        // Keep a copy of all users. Diplay list is filtered using filters
        setInitialUsers(users);
    };

    const userData = async () => {
        setLoading(true);
        setLoadingPerimetre(true);

        utilisateurService
            .getPerimetre()
            .then((response: any) => {
                setPerimetre(response);
                setLoadingPerimetre(false);
            })
            .catch(() => {});

        await Promise.all([
            utilisateurService.getUserConnected().catch(() => {}),
            utilisateurService
                .getUtilisateurs()
                .then((response: any) => response)
                .catch(() => {}),
        ])
            .then(([userConnected, usersList]) => {
                setUserConnected(userConnected);
                handleResponse(usersList);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const validationSchema = FormValidation(selectedUser, 'update');
    const methods = useForm<UserFormData>({
        resolver: yupResolver(validationSchema),
        defaultValues: selectedUser,
    });

    const {
        handleSubmit,
        reset,
        setValue,
        getValues,
        control,
        trigger,
        watch,
        formState: { errors },
        formState: { isDirty },
        setError,
    } = methods;

    const { fields: fieldsCodeGestionnaire, append, remove } = useFieldArray({ name: 'codeGestionnaireMultiple', control });

    const initFormControlled = (initialData: UserFormData) => {
        setTitre(initialData.titre as TypeTitre);
        setRole(initialData.role as TypeRole);
        // Perimetre
        setSante(initialData.perimetre.risques.sante);
        setPrevoyance(initialData.perimetre.risques.prevoyance);
        setSelectedColleges(initialData.perimetre.colleges);
        setSelectedSouscrips(initialData.perimetre.souscripteurs);
        // init useForm
        setSelectedUser({ ...initialData });
    };

    const handleFormCancel = () => {
        initFormControlled({ ...selectedUser });
    };

    const updateUserDataInList = (response: Utilisateur) => {
        // update user data in list
        let updatedList = cloneDeep(initialUsers);
        const user = updatedList.find((u) => u.id === response.id);
        user.accesWeb = response.accesWeb;
        user.fonction = response.fonction;
        user.nom = response.nom;
        user.prenom = response.prenom;
        user.responsable = response.responsable;
        user.role = response.role;

        // update responsable/admin in table list
        if (user.role === 'RESPONSABLE') {
            updatedList = updatedList.map((user) => {
                if (user.responsable?.id === response.id) {
                    return {
                        ...user,
                        responsable: {
                            ...user.responsable,
                            prenom: response.prenom,
                            nom: response.nom,
                        },
                    };
                }
                return user;
            });
        }

        setInitialUsers(updatedList);

        // Update responsables list
        const responsables = responsablesFromUsers(updatedList);
        setResponsables(responsables);
    };

    const handleServerSideErrors = (error) => {
        if (error.response.data?.formErrors) {
            addServerErrors(error.response.data.formErrors, setError);
        }
    };

    const isEmailChanged = async (user) => {
        if (selectedUser.email !== user.email) {
            const response = await utilisateurService.refreshToken();
            setToken(response.token);
        }
    };

    const handleSendForm = async (formData: UserFormData) => {
        await trigger(['email', 'login']);
        setIsFormSubmitted(true);

        // Convert perimeter data
        let risques = [];
        if (formData.perimetre.risques.sante) risques.push('S');
        if (formData.perimetre.risques.prevoyance) risques.push('P');

        const souscripIds = formData.perimetre.souscripteurs.map((d) => d.value);
        const collegeIds = formData.perimetre.colleges.map((d) => d.value);

        const payload: Utilisateur = {
            id: formData.id,
            nom: formData.nom,
            prenom: formData.prenom,
            titre: formData.titre,
            telephone: formData.telephone,
            email: formData.email,
            fonction: formData.fonction,
            role: formData.role as TypeRole,
            responsable: formData.responsable,
            accesWeb: formData.accesWeb === 'true',
            login: formData.login,
            codeGestionnaire: formData.codeGestionnaire,
            codeGestionnaireMultiple: formData.codeGestionnaireMultiple,
            perimetre: {
                risques: risques,
                souscripteurs: souscripIds,
                colleges: collegeIds,
                excludeVIP: formData.perimetre.excludeVIP,
            },
            roles: formData.roles,
            alertBia: {
                id: formData.alertBia.id,
                actif: formData.alertBia.actif,
                frequence: formData.alertBia.frequence,
                lastNotification: formData.alertBia.lastNotification,
            },
        };

        await utilisateurService
            .putUser(payload)
            .then((response) => {
                convertApiResponseToFormData(response);
                // update user data in list
                updateUserDataInList(response);
                if (parseInt(user.id) == formData.id) isEmailChanged(payload);
                //setSelectedUser({ ...formData });
                toast.success('Utilisateur modifié !');
            })
            .catch((errors) => {
                handleServerSideErrors(errors);
            });
        setIsFormSubmitted(false);
    };

    const validateStep = async (activeStep) => {
        let result = false;
        switch (activeStep) {
            case 0:
                result = await trigger(['titre', 'nom', 'prenom', 'telephone', 'email', 'fonction', 'role', 'responsable', 'accesWeb', 'login']);
                return result;
            case 1:
                result = await trigger(['perimetre']);
                return result;
            case 2:
                result = await trigger(['roles']);
                return result;
            case 3:
                result = await trigger(['codeGestionnaireMultiple']);
                return result;
            case 4:
                result = true; //await trigger(['alerte']);
                return result;
            default:
                throw Error('Step validation is missing');
        }
    };

    const confirmUserToDelete = (utilisateur: Utilisateur) => {
        // Check if user is responsable of other
        const isResponsableOfOther = initialUsers.some((user) => user.responsable?.id === utilisateur.id);
        if (isResponsableOfOther) {
            alertError('1', utilisateur);
            return false;
        }
        // oper confirmation modal
        setIsModalOpen(true);
    };

    const deleteAccount = async (utilisateur) => {
        setIsFormdeleting(true);
        await utilisateurService
            .deleteUser(utilisateur)
            .then(async () => {
                // Remove user from list
                const filteredList = users.filter((d) => d.id !== utilisateur.id);
                setUsers(filteredList);
                toast.success('Utilisateur supprimé !');
            })
            .catch((err) => {
                const errorCode = err.response?.data?.code;
                alertError(errorCode, utilisateur);
            })
            .finally(() => {
                setIsModalOpen(false);
                setDrawerOpened(false);
                delay(setIsFormdeleting, 200, false);
            });
    };

    const hasError = (onglet) => {
        const errorFields = Object.keys(errors);
        let hasError = false;

        switch (onglet) {
            case 'infos':
                const infosFields = ['titre', 'nom', 'prenom', 'email', 'fonction', 'login', 'telephone'];
                hasError = infosFields.some((value) => errorFields.includes(value));
                break;
            case 'perimetre':
                const perimetreFields = ['perimetre'];
                hasError = perimetreFields.some((value) => errorFields.includes(value));
                break;
            case 'droits':
                const droitsFields = ['roles'];
                hasError = droitsFields.some((value) => errorFields.includes(value));
                break;
            case 'gestionnaires':
                const gestionnaireFields = ['codeGestionnaireMultiple'];
                hasError = gestionnaireFields.some((value) => errorFields.includes(value));
                break;
            default:
                throw new Error('Tab ' + onglet + ' does not exist');
        }

        return hasError ? styles.msgError : '';
    };

    const actionToolbarToDeleteUser = () => {
        return (
            <Box className="text-right mt-4 mb-4" display="flex" justifyContent="flex-end">
                <Box className="mr-2">
                    <Button variant="contained" color="primary" onClick={() => deleteAccount(selectedUser)} className={commonStyles.btnBlue} disabled={isFormdeleting}>
                        Supprimer l'utilisateur
                    </Button>
                </Box>
                <Button variant="contained" color="primary" className={commonStyles.btnRed} onClick={() => setIsModalOpen(false)} disabled={isFormdeleting}>
                    Fermer
                </Button>
            </Box>
        );
    };

    const alertError = (code, utilisateur) => {
        setErrorCode(code);
        switch (code) {
            case '1':
                const countManaged = initialUsers.filter((u) => u.responsable?.id === utilisateur.id).length;
                const title = `Cet utilisateur est responsable de ${countManaged} autre${countManaged > 1 ? 's' : ''} utilisateur${countManaged > 1 ? 's' : ''}.`;
                setModalErrorTitle(title);
                break;

            case '2':
                const titleUserNotFound = `Cet utilisateur n'est plus enregistré dans l'application !`;
                setModalErrorTitle(titleUserNotFound);
                break;
            default:
                setModalErrorTitle('Oups, une erreur technique est survenue');
                break;
        }
        setIsModalErrorOpen(true);
    };

    const handleExport = () => {
        setDownloading(true);
        exporUtilisateurs(activeFilters)
            .then((data) => {
                const url = window.URL.createObjectURL(
                    new Blob([data as BlobPart], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    }),
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `utilisateurs.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .finally(() => {
                setDownloading(false);
            });
    };

    useEffect(() => {
        userData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleFilters();
    }, [activeFilters, handleFilters]);

    useEffect(() => {
        if (role === TypeRole.ADMIN) {
            setSelectedResponsable(null);
            setValue('responsable', null, { shouldDirty: true });
        }
    }, [role, setValue, setSelectedResponsable]);

    useEffect(
        () => {
            reset({ ...selectedUser });
            setTitre(selectedUser.titre as TypeTitre);
            setRole(selectedUser.role as TypeRole);
            // Perimetre
            setSante(selectedUser.perimetre.risques.sante);
            setPrevoyance(selectedUser.perimetre.risques.prevoyance);
            setSelectedSouscrips(selectedUser.perimetre.souscripteurs);
            setSelectedColleges(selectedUser.perimetre.colleges);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedUser],
    );

    const MsgNoRight = () => <Alert severity="warning">Vous ne disposez pas des droits pour modifier ces informations</Alert>;

    return (
        <>
            <ModalErrorDeletingUser
                isModalErrorOpen={isModalErrorOpen}
                setIsModalErrorOpen={setIsModalErrorOpen}
                errorCode={errorCode}
                modalErrorTitle={modalErrorTitle}
                responsables={responsables}
                utilisateur={selectedUser}
                users={users}
                setInitialUsers={setInitialUsers}
            />
            <Box>
                <Box display="flex" className="my-2" justifyContent="" alignItems="center">
                    <FormProvider {...methods}>
                        <SGSColumnFilter columns={filterableColumnLabels} className="mr-5" filters={activeFilters} onFiltersChange={setActiveFilters} disabled={loading} />
                    </FormProvider>
                </Box>
                <Box display="flex" justifyContent="right">
                    <SGSButton color="primary" onClick={() => handleExport()} icon="far fa-file-excel fa-lg" className={styles.export} loading={downloading}>
                        {t('global.export')}
                    </SGSButton>
                </Box>
                <SGSCard color="default" className="w-100" contentStyle={styles.cardContent}>
                    <SGSTable onRowClick={(row) => handleDetails({ row })} columns={columns} data={users} loading={loading}></SGSTable>
                </SGSCard>
                <SGSDrawer open={drawerOpened} toggleDrawer={toggleDrawer} containerStyle="container-W-914">
                    {!isLoadingUser && (
                        <>
                            <Box className={styles.cancel}>
                                <Box className="text-right mr-5">
                                    {selectedUser.id !== user.id && (
                                        <SGSTextButton variant="outlined" color="orange" onClick={() => confirmUserToDelete(selectedUser)}>
                                            <FontAwesomeIcon icon={faTimesCircle} /> SUPPRIMER
                                        </SGSTextButton>
                                    )}
                                </Box>
                            </Box>
                            <Box className={styles.summary}>
                                <Box className={styles.summary__top}>
                                    <Box display="flex" flexDirection="column" maxWidth="500px">
                                        <Box display="flex" flexDirection="row" alignItems="center" className="mb-1">
                                            <Typography variant="h3">
                                                {selectedUser.prenom} {selectedUser.nom}
                                            </Typography>
                                        </Box>
                                        <Divider className={styles.divider}></Divider>
                                        <Typography>{selectedUser.fonction}</Typography>
                                    </Box>
                                </Box>
                                <>
                                    <SGSTabs
                                        value={selectedTab}
                                        onChange={handleSelectedTab}
                                        TabIndicatorProps={{
                                            style: { height: '0px', top: '0px' },
                                        }}
                                    >
                                        {hasRole(user, ['ROLE_EDIT_USER', 'ROLE_LIST_USER']) && (
                                            <SGSTab backgroundColor={styles.contratBackground} label="INFORMATION" className={classNames(hasError('infos'))}></SGSTab>
                                        )}
                                        {hasRole(user, ['ROLE_EDIT_USER', 'ROLE_LIST_USER']) && (
                                            <SGSTab backgroundColor={styles.contratBackground} label="PERIMETRE" className={classNames(hasError('perimetre'))}></SGSTab>
                                        )}
                                        {hasRole(user, ['ROLE_EDIT_USER', 'ROLE_LIST_USER']) && (
                                            <SGSTab backgroundColor={styles.contratBackground} label="DROITS" className={classNames(hasError('droits'))}></SGSTab>
                                        )}
                                        {hasRole(user, ['ROLE_EDIT_USER', 'ROLE_LIST_USER']) && (
                                            <SGSTab backgroundColor={styles.contratBackground} label="CODE GESTIONNAIRE" className={classNames(hasError('gestionnaires'))}></SGSTab>
                                        )}
                                        {/* {hasRole(user, ['ROLE_EDIT_USER']) && (
                                            <SGSTab backgroundColor={styles.contratBackground} label="NOTIFICATION" className={classNames(hasError('gestionnaires'))}></SGSTab>
                                        )} */}
                                    </SGSTabs>

                                    <SGSTabPanel value={selectedTab} index={0} className={styles.contratWrapper}>
                                        <FormProvider {...methods}>
                                            <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                <FormInfos watch={watch} setValue={setValue} getValues={getValues} mode={'update'} trigger={trigger} />
                                            </SGSForm.Card>
                                        </FormProvider>
                                    </SGSTabPanel>
                                    <SGSTabPanel value={selectedTab} index={1} className={styles.contratWrapper}>
                                        <FormProvider {...methods}>
                                            <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                {!hasRole(user, ['ROLE_ADMIN']) && selectedUser.id === user.id && (
                                                    <Box className="text-center mb-4">
                                                        <MsgNoRight />
                                                    </Box>
                                                )}
                                                <FormPerimetre
                                                    setValue={setValue}
                                                    errors={errors}
                                                    trigger={trigger}
                                                    readOnly={selectedUser.id === user.id || selectedUser.roles.includes('ROLE_ADMIN')}
                                                ></FormPerimetre>
                                            </SGSForm.Card>
                                        </FormProvider>
                                    </SGSTabPanel>
                                    <SGSTabPanel value={selectedTab} index={2} className={classNames(styles.contratWrapper)}>
                                        <FormProvider {...methods}>
                                            <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                {!hasRole(user, ['ROLE_ADMIN']) && selectedUser.id === user.id && (
                                                    <Box className="text-center mb-4">
                                                        <MsgNoRight />
                                                    </Box>
                                                )}
                                                <FormDroits
                                                    errors={errors}
                                                    trigger={trigger}
                                                    setValue={setValue}
                                                    getValues={getValues}
                                                    readOnly={selectedUser.id === user.id || selectedUser.roles.includes('ROLE_ADMIN')}
                                                ></FormDroits>
                                            </SGSForm.Card>
                                        </FormProvider>
                                    </SGSTabPanel>
                                    <SGSTabPanel value={selectedTab} index={3} className={styles.contratWrapper}>
                                        <FormProvider {...methods}>
                                            <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                <FormCodeGestionnaire
                                                    fields={fieldsCodeGestionnaire}
                                                    append={append}
                                                    remove={remove}
                                                    getValues={getValues}
                                                    watch={watch}
                                                    trigger={trigger}
                                                    errors={errors}
                                                />
                                            </SGSForm.Card>
                                        </FormProvider>
                                    </SGSTabPanel>

                                    <SGSTabPanel value={selectedTab} index={4} className={styles.contratWrapper}>
                                        <FormProvider {...methods}>
                                            <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                <FormAlerte lastNotification={selectedUser.alertBia.lastNotification} mode={'update'} />
                                            </SGSForm.Card>
                                        </FormProvider>
                                    </SGSTabPanel>

                                    <SGSForm.Actions>
                                        <FormProvider {...methods}>
                                            <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                <Box className="text-right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <SGSButton type="button" onClick={() => handleFormCancel()} disabled={!isDirty || isVisitorBackOffice(user) || isFormSubmitted ? true : false}>
                                                        {t('global.cancel')}
                                                    </SGSButton>
                                                    <SGSButton type="submit" className="ml-1" disabled={!isDirty || isVisitorBackOffice(user)} loading={isFormSubmitted}>
                                                        {t('global.save')}
                                                    </SGSButton>
                                                </Box>
                                            </SGSForm.Card>
                                        </FormProvider>
                                    </SGSForm.Actions>
                                </>
                            </Box>
                        </>
                    )}
                    {isLoadingUser && (
                        <Box className={styles.loading}>
                            <Loader loading={isLoadingUser} />
                        </Box>
                    )}
                </SGSDrawer>
            </Box>
            <SGSModalBlue isOpenModal={isModalOpen} setIsOpenModal={setIsModalOpen} title={'Supprimer un utilisateur'} actionToolbar={actionToolbarToDeleteUser}>
                {!isFormdeleting ? (
                    <>
                        <Box className="mt-5">
                            <Typography className={commonStyles.txtBlueLightBold}>
                                Confirmez-vous la suppression de l'utilisateur{' '}
                                <span className={commonStyles.txtBlueDarkBold}>
                                    {selectedUser.prenom} {selectedUser.nom}
                                </span>{' '}
                                ?
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className={commonStyles.txtBlueLight}>Cette action est définitive et irréversible.</Typography>
                        </Box>
                    </>
                ) : (
                    <Box className="mt-5">
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={40} style={{ marginRight: '10px' }} />
                            <Typography style={{ marginBottom: '0' }} className={commonStyles.txtBlueLightBold}>
                                En cours de traitement...
                            </Typography>
                        </Box>
                    </Box>
                )}
            </SGSModalBlue>
        </>
    );
};

function getStatusIcon(value: string, faSize: SizeProp = 'lg', padding: string = '4', text?: string) {
    let icon = null;
    switch (value) {
        case 'ACTIF':
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.affiliateIcon)}>
                    <FontAwesomeIcon icon={faFile} />
                    <FontAwesomeIcon icon={faCheckCircle} transform="shrink-8 down-4 right-7" stroke="white" strokeWidth="15" />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.affiliateIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;
        default:
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.biaIcon)}>
                    <FontAwesomeIcon icon={faFile} />
                    <FontAwesomeIcon icon={faTimesCircle} transform="shrink-8 down-4 right-3" stroke="white" strokeWidth="15" />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.affiliateIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;
    }
    return icon;
}

const souscripOptionsAndSelection = (souscripteurs, risks, perimetre, isAdmin) => {
    let souscripOpts = [];
    const souscriptIds = souscripteurs ?? [];
    perimetre.forEach((item) => {
        // Constraints
        const isFiltered = risks?.length === 0 ? false : true;
        // Get data
        if (risks.includes(item.typ) || !isFiltered) souscripOpts.push({ value: item.codSouscrip, label: item.souscrip });
    });
    souscripOpts = makeUniqueAndSort(souscripOpts);
    const souscripOptsSelected = souscripOpts.filter((opt) => souscriptIds.includes(opt.value) || isAdmin);

    return { souscriptIds, souscripOpts, souscripOptsSelected };
};

const collegesOptionsAndSelection = (colleges, risks, souscriptIds, perimetre, isAdmin) => {
    let collegeOpts = [];
    const collegeIds = colleges ?? [];
    perimetre.forEach((item) => {
        // Constraints
        const isFiltered = risks.length === 0 ? false : true;
        const result = [true];
        result.push(risks.includes(item.typ));
        result.push(souscriptIds.includes(item.codSouscrip));
        // Get data
        if (!result.includes(false) || !isFiltered) collegeOpts.push({ value: item.codcol, label: item.college });
    });
    collegeOpts = makeUniqueAndSort(collegeOpts);
    let collegeOptsSelected = collegeOpts.filter((opt) => collegeIds.includes(opt.value) || isAdmin);

    return { collegeIds, collegeOpts, collegeOptsSelected };
};

export default UsersList;
